import React from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
} from '@mui/material'
import { BugReport, Feedback as FeedbackIcon } from '@mui/icons-material'
import { useGetUserFeedbackQuery, FeedbackTicket } from '@store/api/feedbackApi'

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  open: 'primary',
  in_progress: 'warning',
  resolved: 'success',
  closed: 'default',
}

const TicketCard: React.FC<{ ticket: FeedbackTicket }> = ({ ticket }) => (
  <Card variant="outlined" sx={{ mb: 2 }}>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box display="flex" alignItems="center" gap={1}>
          {ticket.type === 'bug' ? <BugReport color="error" fontSize="small" /> : <FeedbackIcon color="primary" fontSize="small" />}
          <Typography variant="subtitle1" fontWeight="bold">{ticket.subject}</Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Chip label={ticket.referenceNumber} size="small" variant="outlined" />
          <Chip label={ticket.status.replace('_', ' ')} size="small" color={STATUS_COLORS[ticket.status] || 'default'} />
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {ticket.description}
      </Typography>

      <Box display="flex" gap={1} flexWrap="wrap">
        {ticket.severity && <Chip label={`Severity: ${ticket.severity}`} size="small" />}
        {ticket.category && <Chip label={ticket.category} size="small" variant="outlined" />}
        <Typography variant="caption" color="text.secondary">
          Submitted {new Date(ticket.createdAt).toLocaleDateString()}
        </Typography>
      </Box>

      {ticket.resolution && (
        <Box sx={{ mt: 1, p: 1, bgcolor: 'success.50', borderRadius: 1 }}>
          <Typography variant="caption" color="success.main" fontWeight="bold">Resolution: </Typography>
          <Typography variant="caption">{ticket.resolution}</Typography>
        </Box>
      )}
    </CardContent>
  </Card>
)

const FeedbackList: React.FC = () => {
  const { data: tickets, isLoading, error } = useGetUserFeedbackQuery()

  if (isLoading) return <LinearProgress />
  if (error) return <Alert severity="error">Failed to load your reports</Alert>
  if (!tickets?.length) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="body1" color="text.secondary">
          You haven&apos;t submitted any reports yet.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      {tickets.map((ticket) => (
        <TicketCard key={ticket.id} ticket={ticket} />
      ))}
    </Box>
  )
}

export default FeedbackList
